import AddRounded from '@mui/icons-material/AddRounded';
import DeleteIcon from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { BaseModal } from '@src/common/components/BaseModal';
import { Button } from '@src/common/components/button/Button';
import { FilterableTable } from '@src/common/components/FilterableTable';
import { IconButton } from '@src/common/components/IconButton';
import { LoadingSpinner } from '@src/common/components/LoadingSpinner';
import { CloseConfirmModalFooter } from '@src/common/components/modal/CloseConfirmModalFooter';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { useToast } from '@src/common/hooks/useToast';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { ClientsNavigation } from '@src/routing/ClientsNavigation';
import { useDeleteTractor } from '@src/tractor/hooks/useDeleteTractor';
import { useTractors } from '@src/tractor/hooks/useTractors';
import { t } from 'i18next';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

type Props = {
  clientId: string;
};

export const TractorsList = ({ clientId }: Props) => {
  const {
    tractors: { tractors },
    isLoading,
  } = useTractors(clientId);
  const navigate = useNavigate();

  const isEmptyList = tractors.length < 1;

  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center">
        <LoadingSpinner />
      </Stack>
    );
  }

  const columns: GridColDef[] = [
    { field: 'unitNumber', headerName: t('unitNumber'), flex: 1 },
    { field: 'licensePlate', headerName: t('licensePlate'), flex: 1 },
    { field: 'year', headerName: t('year'), flex: 1 },
    { field: 'make', headerName: t('make'), flex: 1 },
    {
      field: 'Actions',
      headerName: '',
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      hideable: false,
      renderCell: (params) => {
        return (
          <>
            <IconButton
              onClick={() =>
                navigate(
                  ClientsNavigation.tractors.edit.url(clientId, params.row.id),
                )
              }
              title={t('editTractor')}
            >
              <Edit />
            </IconButton>
            <DeleteTractorButton
              clientId={clientId}
              tractorId={params.row.id}
            />
          </>
        );
      },
    },
  ];

  const rows: Record<string, string>[] = tractors.map((tractor) => {
    return {
      id: tractor.id,
      unitNumber: tractor.unitNumber,
      year: tractor.year.toString(),
      make: tractor.make,
      licensePlate: tractor.licensePlate,
    };
  });

  return (
    <>
      <Stack alignItems="flex-end" className={addTractorButtonContainer}>
        <Button
          className={addTractorButton}
          variant="contained"
          onClick={() => navigate(ClientsNavigation.tractors.add.url(clientId))}
          startIcon={<AddRounded />}
        >
          {t('addTractor')}
        </Button>
      </Stack>
      {isEmptyList && (
        <Typography className={emptyList} variant="body2">
          {t('emptyTractorList')}
        </Typography>
      )}
      {!isEmptyList && (
        <FilterableTable
          ariaLabel="tractors-list"
          columns={columns}
          rows={rows}
          paginationAndSortMode="client"
        />
      )}
    </>
  );
};

const DeleteTractorButton = ({
  clientId,
  tractorId,
}: {
  clientId: string;
  tractorId: string;
}) => {
  const [open, setOpen] = useState(false);
  const { showErrorToast, showSuccessToast } = useToast();

  const { deleteTractor, isLoading } = useDeleteTractor({
    clientId,
    tractorId,
    onSuccess: () => {
      setOpen(false);
      showSuccessToast(t('deleteTractor.success'));
    },
    onError: () => {
      showErrorToast(t('deleteTractor.error'));
    },
  });

  return (
    <>
      <IconButton
        onClick={() => setOpen(true)}
        title={t('deleteTractor.title')}
      >
        <DeleteIcon />
      </IconButton>
      <BaseModal
        open={open}
        onClose={() => setOpen(false)}
        title={t('deleteTractor.title')}
      >
        <Stack gap={theme.spacing['3']}>
          <Typography>{t('deleteTractor.message')}</Typography>
          <CloseConfirmModalFooter
            onClose={() => setOpen(false)}
            confirm={() => deleteTractor()}
            closeLabel={t('cancel')}
            confirmLabel={t('delete')}
            confirmLoading={isLoading}
            confirmDisabled={isLoading}
          />
        </Stack>
      </BaseModal>
    </>
  );
};

const emptyList = css({
  marginLeft: theme.spacing['4'],
});

const addTractorButtonContainer = css({
  marginBottom: theme.spacing['3'],
  marginTop: theme.spacing['3'],
});

const addTractorButton = css({
  backgroundColor: theme.palette.primary.primary,
  borderRadius: '8px',
});
